import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getEmployeeDetails } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_EMPLOYEES,
    SOCIETY_PORTAL_EMPLOYEES_EDIT,
    SOCIETY_PORTAL_EMPLOYEES_WORK_LOG,
} from '../../../Routing/routes';
import {
    MARITAL_STATUS_OPTIONS,
    BANK_ACCOUNT_TYPE_OPTIONS,
    maskValue,
    optionLabel,
    formatPlainDate,
} from '../../../consts/employeeProfileFields';
import {
    formatDateOnly,
    shiftTimeRange,
    offDayLabel,
} from '../../../consts/shiftConstants';
import { formatIstDate } from '../../../utils/helpers/istDate';

const InfoCard = ({ icon, label, value }) => (
    <div className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-border-gray-100">
        <div className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
            <Icon icon={icon} className="tw-text-base" />
        </div>
        <div className="tw-flex-1 tw-min-w-0">
            <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-0.5">{label}</p>
            <p className="tw-text-xs tw-text-gray-800 tw-font-medium tw-break-words">{value || '-'}</p>
        </div>
    </div>
);

const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    return formatIstDate(dt);
};

const ViewEmployee = () => {
    const navigate = useNavigate();
    const { can } = usePermissions();
    const dispatch = useDispatch();
    const { id } = useParams();

    const {
        employeeDetailsData: emp,
        employeeDetailsLoading,
    } = useSelector(state => state.EmployeeReducer || {});

    const refreshKey = usePageRefreshKey();

    useEffect(() => {
        if (id) dispatch(getEmployeeDetails({ id, societyId: selectedSocietyStorer.getSocietyId() }));
    }, [dispatch, id, refreshKey]);

    const fullName = emp
        ? (emp.name || [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim() || '-')
        : '-';
    const isActive = emp?.is_active === 1 || emp?.is_active === true
        || (emp?.status || '').toLowerCase() === 'active';
    const employeeId = emp?.employee_id ?? emp?.id;

    // PAN, Aadhaar and account number come back unmasked — mask by default.
    const [showSensitive, setShowSensitive] = useState(false);
    const sensitive = (v) => (showSensitive ? v : maskValue(v));

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                        className="tw-p-2 tw-rounded-md tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Employee Details</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Read-only view</p>
                    </div>
                </div>
                {emp && (
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {/* This employee's activity: actions, logins, punches and work entries. */}
                        {can(PERMISSIONS.EMPLOYEE_WORK_LOG) && (
                            <button
                                onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_EMPLOYEES_WORK_LOG}?employeeId=${employeeId}`)}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-indigo-700 tw-border tw-border-indigo-200 tw-bg-indigo-50 hover:tw-bg-indigo-100"
                            >
                                <Icon icon="mdi:timeline-clock-outline" className="tw-text-base" />
                                Work Log
                            </button>
                        )}
                        <button
                            onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES_EDIT.replace(':id', employeeId))}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                        >
                            <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                            Edit
                        </button>
                    </div>
                )}
            </div>

            {employeeDetailsLoading && !emp ? (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-p-12 tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                    <span className="tw-text-xs tw-text-gray-400">Loading employee...</span>
                </div>
            ) : !emp ? (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-p-12 tw-text-center tw-text-xs tw-text-gray-400">
                    Employee not found.
                </div>
            ) : (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    {/* Profile header */}
                    <div className="tw-p-5 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-4">
                        <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gradient-to-br tw-from-rose-500 tw-to-rose-700 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-xl tw-font-bold tw-flex-shrink-0">
                            {fullName.charAt(0).toUpperCase()}
                        </div>
                        <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                <h2 className="tw-text-base tw-font-bold tw-text-gray-800">{fullName}</h2>
                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${isActive ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                                {emp.employeeType?.type || emp.type || '—'}
                                {emp.username ? ` · @${emp.username}` : ''}
                            </p>
                            <p className="tw-text-[11px] tw-text-gray-400 tw-font-mono tw-mt-0.5">{emp.employee_code || '-'}</p>
                        </div>
                    </div>

                    {/* Sections */}
                    <div className="tw-p-5">
                        <h3 className="tw-text-xs tw-font-bold tw-text-gray-700 tw-mb-3 tw-uppercase tw-tracking-wide">Contact</h3>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3 tw-mb-5">
                            <InfoCard icon="mdi:email-outline" label="Email" value={emp.email} />
                            <InfoCard icon="mdi:phone-outline" label="Phone" value={emp.phone} />
                            <InfoCard icon="mdi:phone-plus-outline" label="Alternate Phone" value={emp.alternate_phone} />
                            <div className="sm:tw-col-span-2">
                                <InfoCard icon="mdi:map-marker-outline" label="Address" value={emp.address} />
                            </div>
                        </div>

                        <h3 className="tw-text-xs tw-font-bold tw-text-gray-700 tw-mb-3 tw-uppercase tw-tracking-wide">Employment</h3>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                            <InfoCard icon="mdi:account-tie-outline" label="Employee Type" value={emp.employeeType?.type || emp.type} />
                            <InfoCard icon="mdi:briefcase-variant-outline" label="Employment Type" value={emp.employment_type} />
                            <InfoCard icon="mdi:calendar-outline" label="Joining Date" value={formatDate(emp.joining_date || emp.date_of_joining)} />
                            <InfoCard icon="mdi:badge-account-outline" label="Designation" value={emp.designation} />
                            <InfoCard icon="mdi:domain" label="Department" value={emp.department} />
                            <InfoCard icon="mdi:calendar-remove-outline" label="Leaving Date" value={formatDate(emp.leaving_date)} />
                            {/* `shift_assignment` is the assignment in force TODAY, or null.
                                It rides on every single-employee response. */}
                            <InfoCard
                                icon="mdi:clock-outline"
                                label="Shift"
                                value={emp.shift_assignment
                                    ? `${emp.shift_assignment.shift?.shift_name || `#${emp.shift_assignment.shift_id}`} · ${shiftTimeRange(emp.shift_assignment.shift)}`
                                    : 'No shift assigned'}
                            />
                            {emp.shift_assignment && (
                                <>
                                    <InfoCard
                                        icon="mdi:calendar-start-outline"
                                        label="Shift Since"
                                        value={formatDateOnly(emp.shift_assignment.effective_from)}
                                    />
                                    <InfoCard
                                        icon="mdi:calendar-weekend-outline"
                                        label="Weekly Offs"
                                        value={offDayLabel(emp.shift_assignment)}
                                    />
                                </>
                            )}
                        </div>

                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-5 tw-mb-3">
                            <h3 className="tw-text-xs tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wide">Personal Information</h3>
                            <button
                                type="button"
                                onClick={() => setShowSensitive(s => !s)}
                                className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-rose-600 hover:tw-text-rose-700"
                            >
                                <Icon icon={showSensitive ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                                {showSensitive ? 'Hide' : 'Show'} PAN / Aadhaar / Account No.
                            </button>
                        </div>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3 tw-mb-5">
                            <InfoCard icon="mdi:cake-variant-outline" label="Date of Birth" value={formatPlainDate(emp.date_of_birth)} />
                            <InfoCard icon="mdi:ring" label="Marital Status" value={optionLabel(MARITAL_STATUS_OPTIONS, emp.marital_status)} />
                            <InfoCard icon="mdi:water-outline" label="Blood Group" value={emp.blood_group} />
                            <InfoCard icon="mdi:flag-outline" label="Nationality" value={emp.nationality} />
                            <InfoCard icon="mdi:school-outline" label="Qualification" value={emp.qualification} />
                            <InfoCard icon="mdi:card-account-details-outline" label="PAN Number" value={sensitive(emp.pan_number)} />
                            <InfoCard icon="mdi:card-account-details-star-outline" label="Aadhaar Number" value={sensitive(emp.aadhar_number)} />
                        </div>

                        <h3 className="tw-text-xs tw-font-bold tw-text-gray-700 tw-mb-3 tw-uppercase tw-tracking-wide">Bank Details</h3>
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                            <InfoCard icon="mdi:account-cash-outline" label="Account Holder Name" value={emp.bank_account_holder_name} />
                            <InfoCard icon="mdi:bank-outline" label="Bank Name" value={emp.bank_name} />
                            <InfoCard icon="mdi:wallet-outline" label="Account Type" value={optionLabel(BANK_ACCOUNT_TYPE_OPTIONS, emp.bank_account_type)} />
                            <InfoCard icon="mdi:numeric" label="Account Number" value={sensitive(emp.bank_account_number)} />
                            <InfoCard icon="mdi:barcode" label="IFSC Code" value={emp.bank_ifsc_code} />
                            <InfoCard icon="mdi:source-branch" label="Branch Name" value={emp.bank_branch_name} />
                            <InfoCard icon="mdi:qrcode" label="UPI ID" value={emp.upi_id} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewEmployee;
